import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey/survey";
import { captureEvent } from "@/lib/analytics";

export const useEmailCampaignsStore = defineStore("emailCampaigns", () => {
  // state
  const emailCampaigns = ref([]);
  const emailCampaignsCount = ref(0);
  const emailCampaignsSearchQuery = ref("");
  const emailCampaignsStatusFilter = ref([]);
  const currentPage = ref(1);

  // stores
  const { token } = storeToRefs(useSurveyStore());

  // actions
  const getEmailCampaigns = async (page = 1) => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/email_campaigns?page=${page}`).json();

      if (response.value.ok) {
        const { email_campaigns: campaignsData, email_campaigns_count: campaignsCountData } = data.value;
        emailCampaigns.value = campaignsData;
        emailCampaignsCount.value = campaignsCountData;
        currentPage.value = page;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const searchEmailCampaigns = async (page = 1) => {
    const params = {
      name: emailCampaignsSearchQuery.value,
      status: emailCampaignsStatusFilter.value,
      page: page,
    };
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/email_campaigns/search`)
        .json()
        .post(params, null);

      if (response.value.ok) {
        const { email_campaigns: campaignsData, email_campaigns_count: campaignsCountData } = data.value;
        emailCampaigns.value = campaignsData;
        emailCampaignsCount.value = campaignsCountData;
        currentPage.value = page;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const duplicateEmailCampaign = async (campaignToken) => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/email_campaigns/${campaignToken}/duplicate`)
        .post()
        .json();

      if (response.value.ok) {
        emailCampaigns.value.splice(0, 0, data.value);
        captureEvent("email_campaign_duplicated");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const removeEmailCampaign = async (campaignToken) => {
    try {
      const { response } = await useMyFetch(`/surveys/${token.value}/email_campaigns/${campaignToken}`).delete().json();

      if (response.value.ok) {
        const listIndex = emailCampaigns.value.findIndex((c) => c.token === campaignToken);
        if (listIndex !== -1) emailCampaigns.value.splice(listIndex, 1);

        captureEvent("email_campaign_deleted");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const suspendEmailCampaign = async (campaignToken) => {
    try {
      const { response } = await useMyFetch(`/surveys/${token.value}/email_campaigns/${campaignToken}/suspend`)
        .post({ client_id: getClientId() })
        .json();

      if (response.value.ok) {
        const listIndex = emailCampaigns.value.findIndex((c) => c.token === campaignToken);
        if (listIndex !== -1) emailCampaigns.value[listIndex].status = "suspended";

        captureEvent("email_campaign_suspended");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const changePage = async (newPage) => {
    currentPage.value = newPage;
    await searchEmailCampaigns(newPage);
  };

  const clearFilters = async () => {
    emailCampaignsStatusFilter.value = [];
    await searchEmailCampaigns();
  };

  const resetAllFilters = () => {
    resetSearchQuery();
    emailCampaignsStatusFilter.value = [];
    currentPage.value = 1;
  };

  // email campaigns channel handlers
  const brevoChannelHandlers = (cable, surveyToken) => {
    return {
      connected: subscribeToChannel(cable, surveyToken),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable, surveyToken) => () =>
    cable.perform({ channel: "BrevoChannel", action: "subscribe", data: { survey_token: surveyToken } });

  const handleReceivedMessage = (msg) => {
    const campaignsToUpdate = emailCampaigns.value.find((campaign) => campaign.token === msg.email_campaign_token);
    if (campaignsToUpdate) {
      if (msg.status) campaignsToUpdate.status = msg.status;
      if (msg.targeted) campaignsToUpdate.statistics.targeted = msg.targeted;
    }
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(emailCampaignsSearchQuery, () => searchEmailCampaigns());

  return {
    // state
    emailCampaigns,
    emailCampaignsSearchQuery,
    emailCampaignsStatusFilter,
    emailCampaignsCount,
    currentPage,
    // actionss
    changePage,
    clearFilters,
    resetAllFilters,
    getEmailCampaigns,
    removeEmailCampaign,
    searchEmailCampaigns,
    suspendEmailCampaign,
    duplicateEmailCampaign,
    brevoChannelHandlers,
  };
});
